"use client";
import React, { useState, useRef, useEffect } from "react";
import { Send, Bot, User, Sparkles, Plus, Database } from "lucide-react";

type Message = {
    id: string;
    role: "user" | "assistant";
    content: string;
    action?: string;
};

export const InteractiveChat = () => {
    const [input, setInput] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [messages, setMessages] = useState<Message[]>([
        {
            id: "1",
            role: "assistant",
            content: "Hi! I'm your AI Schema Architect. I can help you modify your database. Try asking me to 'Add a status field to Task' or 'Connect User and Project'."
        }
    ]);
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, isTyping]);

    const handleSend = () => {
        if (!input.trim()) return;

        const userMsg: Message = {
            id: Date.now().toString(),
            role: "user",
            content: input
        };

        setMessages(prev => [...prev, userMsg]);
        setInput("");
        setIsTyping(true);

        // Simulate AI processing
        setTimeout(() => {
            let responseContent = "I've updated the schema based on your request.";
            let action = "";

            const lowerInput = userMsg.content.toLowerCase();
            if (lowerInput.includes("add") && lowerInput.includes("field")) {
                responseContent = "Done! I've added the new field to your model. The schema has been updated.";
                action = "Added field";
            } else if (lowerInput.includes("connect") || lowerInput.includes("relation")) {
                responseContent = "I've established a relationship between those models. It's a One-to-Many connection.";
                action = "Created relationship";
            } else if (lowerInput.includes("create") && lowerInput.includes("model")) {
                responseContent = "I've created the new model for you with default ID and timestamps.";
                action = "Created model";
            }

            const aiMsg: Message = {
                id: (Date.now() + 1).toString(),
                role: "assistant",
                content: responseContent,
                action
            };

            setMessages(prev => [...prev, aiMsg]);
            setIsTyping(false);
        }, 1500);
    };

    return (
        <div className="w-full max-w-md mx-auto bg-surface border border-border rounded-xl overflow-hidden shadow-2xl flex flex-col h-[400px]">
            {/* Header */}
            <div className="bg-background/50 backdrop-blur-sm p-3 border-b border-border flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-primary/10 rounded-lg">
                        <Bot className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                        <span className="font-semibold text-primary-text text-sm block">Schema Architect</span>
                        <span className="text-[10px] text-green-500 flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                            Online
                        </span>
                    </div>
                </div>
            </div>

            {/* Chat Area */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-surface to-background scroll-smooth">
                {messages.map((msg) => (
                    <div
                        key={msg.id}
                        className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`}
                    >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${msg.role === "user" ? "bg-primary text-white" : "bg-primary/10 text-primary"
                            }`}>
                            {msg.role === "user" ? <User className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
                        </div>
                        <div className={`flex flex-col gap-1 max-w-[80%] ${msg.role === "user" ? "items-end" : "items-start"}`}>
                            <div className={`p-3 rounded-2xl text-sm ${msg.role === "user"
                                    ? "bg-primary text-white rounded-tr-none"
                                    : "bg-surface border border-border text-secondary-text rounded-tl-none"
                                }`}>
                                {msg.content}
                            </div>
                            {msg.action && (
                                <div className="flex items-center gap-1 text-[10px] text-primary font-medium bg-primary/5 px-2 py-0.5 rounded-full border border-primary/10">
                                    <Database className="w-3 h-3" />
                                    {msg.action}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {isTyping && (
                    <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <Sparkles className="w-4 h-4 text-primary" />
                        </div>
                        <div className="bg-surface border border-border p-3 rounded-2xl rounded-tl-none flex items-center gap-1">
                            <div className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce [animation-delay:-0.3s]" />
                            <div className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce [animation-delay:-0.15s]" />
                            <div className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce" />
                        </div>
                    </div>
                )}
            </div>

            {/* Input Area */}
            <div className="p-3 bg-background border-t border-border shrink-0">
                <div className="relative flex items-center gap-2">
                    <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSend()}
                        placeholder="Type a command..."
                        className="flex-1 bg-surface border border-border rounded-lg px-4 py-2.5 text-sm text-primary-text placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                    />
                    <button
                        onClick={handleSend}
                        disabled={!input.trim() || isTyping}
                        className="p-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Send className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};
